import './game.css';
import { relatedToolsMarkup, siteBrandMarkup } from '../site-chrome';
import { STEALTH_LEVELS, STEALTH_WORLD_HEIGHT, STEALTH_WORLD_WIDTH } from './levels';
import {
  calculateStealthStars,
  clamp,
  distanceBetween,
  findGridPath,
  formatStealthSeconds,
  hasLineOfSight,
  isBetterStealthResult,
  pointInRect,
  pointInVisionCone,
  rectCenter,
  rectsOverlap,
} from './logic';
import type {
  BossDefinition,
  Distractor,
  Furniture,
  LightZone,
  Point,
  Rect,
  StealthLevel,
  StealthLevelResult,
  StealthStarRating,
} from './types';

type GameStatus = 'intro' | 'playing' | 'dead' | 'complete' | 'finished';

type Player = {
  x: number;
  y: number;
  w: number;
  h: number;
  facing: number;
};

type RuntimeBoss = {
  definition: BossDefinition;
  x: number;
  y: number;
  angle: number;
  targetIndex: number;
  pauseLeft: number;
  investigateTarget: Point | null;
  investigateLeft: number;
  scanDirection: -1 | 1;
  route: Point[];
  routeGoal: Point | null;
};

type NoisePulse = Point & {
  age: number;
  radius: number;
  maxRadius: number;
};

const PLAYER_WIDTH = 26;
const PLAYER_HEIGHT = 30;
const WALK_SPEED = 198;
const SNEAK_SPEED = 112;
const RETRY_DELAY_MS = 1_150;
const STORAGE_KEY = 'oneultool-stealth-results-v1';

function roundedRect(context: CanvasRenderingContext2D, rect: Rect, radius: number): void {
  context.beginPath();
  context.roundRect(rect.x, rect.y, rect.w, rect.h, Math.min(radius, rect.w / 2, rect.h / 2));
}

function furnitureLabel(kind: Furniture['kind']): string {
  if (kind === 'cabinet') return '서랍';
  if (kind === 'sofa') return '소파';
  if (kind === 'plant') return '화분';
  if (kind === 'partition') return '파티션';
  return '책상';
}

function loadStoredResults(): Map<number, StealthLevelResult> {
  const results = new Map<number, StealthLevelResult>();
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<string, Partial<StealthLevelResult>>;
    for (const [key, value] of Object.entries(parsed)) {
      const index = Number(key);
      if (!Number.isInteger(index) || index < 0 || index >= STEALTH_LEVELS.length) continue;
      if (![1, 2, 3].includes(value.stars ?? 0)) continue;
      if (![value.clearSeconds, value.retries, value.alerts].every((item) => typeof item === 'number' && Number.isFinite(item))) continue;
      results.set(index, value as StealthLevelResult);
    }
  } catch {
    // 저장소를 사용할 수 없는 브라우저에서는 현재 탭 기록만 사용합니다.
  }
  return results;
}

function saveStoredResults(results: ReadonlyMap<number, StealthLevelResult>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(results)));
  } catch {
    // 게임 진행은 저장 실패와 무관하게 계속됩니다.
  }
}

function renderStars(stars: StealthStarRating): string {
  return Array.from({ length: 3 }, (_, index) => `<span class="${index < stars ? 'earned' : ''}">★</span>`).join('');
}

export function mountStealthGame(): void {
  const app = document.querySelector<HTMLDivElement>('#app');
  if (!app) throw new Error('게임 화면을 찾지 못했습니다.');

  document.title = '사장님 몰래 퇴근하기 | 오늘툴';
  document.documentElement.classList.add('stealth-game-page');
  app.innerHTML = `
    <div class="stealth-shell">
      <header class="site-brand-header stealth-header">${siteBrandMarkup()}</header>
      <main class="stealth-main">
        <section class="stealth-intro">
          <span>20 LEVELS · STEALTH MODE</span>
          <h1>사장님 몰래 <em>퇴근하기</em></h1>
          <p>책상 뒤에 숨고, 복사기로 시선을 돌리고, 사장님이 보기 전에 퇴근하세요.</p>
        </section>
        <section class="stealth-game-card" aria-label="사장님 몰래 퇴근하기 게임">
          <div class="stealth-toolbar">
            <div class="stealth-level-copy">
              <span id="stealth-level-number">LEVEL 1 / ${STEALTH_LEVELS.length}</span>
              <strong id="stealth-level-title">${STEALTH_LEVELS[0].title}</strong>
              <small id="stealth-star-target">★★★ ${formatStealthSeconds(STEALTH_LEVELS[0].targetSeconds)} · 경계 0 · 무실패</small>
            </div>
            <div class="stealth-stats">
              <span aria-label="현재 도전 시간">시간 <strong id="stealth-time">0.0초</strong></span>
              <span aria-label="현재 경계 횟수">경계 <strong id="stealth-alerts">0</strong></span>
              <span aria-label="현재 레벨 재도전 횟수">재도전 <strong id="stealth-retries">0</strong></span>
            </div>
          </div>
          <div class="stealth-alert" aria-label="들킬 위험 수치">
            <span id="stealth-danger-copy">들킬 위험</span>
            <i><b id="stealth-danger-bar"></b></i>
          </div>
          <div class="stealth-stage">
            <canvas id="stealth-canvas" width="${STEALTH_WORLD_WIDTH}" height="${STEALTH_WORLD_HEIGHT}" tabindex="0" aria-label="사장님의 시야를 피해 퇴근 문으로 이동하는 사무실 잠입 게임"></canvas>
            <div id="stealth-overlay" class="stealth-overlay"></div>
            <div id="stealth-message" class="stealth-message" role="status" aria-live="polite"></div>
          </div>
          <div class="stealth-mobile-controls" aria-label="모바일 게임 조작">
            <div class="stealth-pad">
              <button type="button" data-stealth-move="up" aria-label="위로 이동">↑</button>
              <button type="button" data-stealth-move="left" aria-label="왼쪽으로 이동">←</button>
              <button type="button" data-stealth-move="down" aria-label="아래로 이동">↓</button>
              <button type="button" data-stealth-move="right" aria-label="오른쪽으로 이동">→</button>
            </div>
            <div class="stealth-action-controls">
              <button id="stealth-sneak" class="stealth-sneak" type="button">살금</button>
              <button id="stealth-action" class="stealth-action" type="button">교란</button>
            </div>
          </div>
          <div class="stealth-levels" aria-label="레벨 선택">
            ${STEALTH_LEVELS.map((item, index) => `<button type="button" data-stealth-level="${index}" class="${index === 0 ? 'active' : ''}" aria-label="${item.number}단계 ${item.title}"><span>${item.number}</span><small data-stealth-stars>···</small></button>`).join('')}
          </div>
          <p class="stealth-help"><kbd>방향키</kbd> 이동 <kbd>Shift</kbd> 살금살금 <kbd>E</kbd> 복사기·커피머신 작동 <kbd>R</kbd> 재도전</p>
        </section>
        ${relatedToolsMarkup('boss-is-watching')}
      </main>
    </div>`;

  const canvas = document.querySelector<HTMLCanvasElement>('#stealth-canvas')!;
  const context = canvas.getContext('2d')!;
  const overlay = document.querySelector<HTMLDivElement>('#stealth-overlay')!;
  const message = document.querySelector<HTMLDivElement>('#stealth-message')!;
  const levelNumber = document.querySelector<HTMLElement>('#stealth-level-number')!;
  const levelTitle = document.querySelector<HTMLElement>('#stealth-level-title')!;
  const starTarget = document.querySelector<HTMLElement>('#stealth-star-target')!;
  const timeDisplay = document.querySelector<HTMLElement>('#stealth-time')!;
  const alertDisplay = document.querySelector<HTMLElement>('#stealth-alerts')!;
  const retryDisplay = document.querySelector<HTMLElement>('#stealth-retries')!;
  const dangerBar = document.querySelector<HTMLElement>('#stealth-danger-bar')!;
  const dangerCopy = document.querySelector<HTMLElement>('#stealth-danger-copy')!;
  if (!canvas || !context || !overlay || !message || !levelNumber || !levelTitle || !starTarget || !timeDisplay || !alertDisplay || !retryDisplay || !dangerBar || !dangerCopy) {
    throw new Error('게임 구성 요소를 준비하지 못했습니다.');
  }

  const input = new Set<string>();
  const bestResults = loadStoredResults();
  const player: Player = { x: 0, y: 0, w: PLAYER_WIDTH, h: PLAYER_HEIGHT, facing: 0 };
  let currentLevelIndex = 0;
  let currentLevel: StealthLevel = STEALTH_LEVELS[0];
  let runtimeBosses: RuntimeBoss[] = [];
  let status: GameStatus = 'intro';
  let levelRetries = 0;
  let runAlerts = 0;
  let totalRetries = 0;
  let detection = 0;
  let warningActive = false;
  let attemptStartedAt = 0;
  let attemptSeconds = 0;
  let lastFrameAt = performance.now();
  let displayedTenths = -1;
  let messageTime = 0;
  let retryReady = false;
  let resetVersion = 0;
  let noisePulses: NoisePulse[] = [];
  let occupiedNoiseZones = new Set<string>();
  let distractorCooldowns = new Map<string, number>();

  function createRuntimeBosses(): RuntimeBoss[] {
    return currentLevel.bosses.map((definition, index) => {
      const start = definition.path[0];
      const next = definition.path[1];
      return {
        definition,
        x: start.x,
        y: start.y,
        angle: Math.atan2(next.y - start.y, next.x - start.x),
        targetIndex: 1,
        pauseLeft: index * .08,
        investigateTarget: null,
        investigateLeft: 0,
        scanDirection: index % 2 === 0 ? 1 : -1,
        route: [],
        routeGoal: null,
      };
    });
  }

  function collisionRects(): Rect[] {
    return [...currentLevel.furniture, ...currentLevel.distractors];
  }

  function updateLevelNavigation(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-stealth-level]').forEach((button) => {
      const index = Number(button.dataset.stealthLevel);
      const result = bestResults.get(index);
      const stars = button.querySelector<HTMLElement>('[data-stealth-stars]');
      if (stars) stars.textContent = result ? '★'.repeat(result.stars) : '···';
      button.classList.toggle('active', index === currentLevelIndex);
      button.classList.toggle('completed', Boolean(result));
      button.setAttribute('aria-current', index === currentLevelIndex ? 'step' : 'false');
      button.setAttribute('aria-label', `${STEALTH_LEVELS[index].number}단계 ${STEALTH_LEVELS[index].title}${result ? `, 최고 별 ${result.stars}개` : ', 미완료'}`);
    });
  }

  function updateStats(): void {
    alertDisplay.textContent = String(runAlerts);
    retryDisplay.textContent = String(levelRetries);
    dangerBar.style.width = `${Math.round(detection * 100)}%`;
    dangerBar.classList.toggle('danger', detection >= .68);
    dangerCopy.textContent = detection >= .68 ? '곧 들킵니다!' : detection >= .3 ? '누가 보고 있어요' : '들킬 위험';
  }

  function showMessage(copy: string, duration = 1.8): void {
    message.textContent = copy;
    message.classList.add('visible');
    messageTime = duration;
  }

  function hideOverlay(): void {
    overlay.classList.remove('visible');
    overlay.innerHTML = '';
  }

  function showIntroOverlay(): void {
    overlay.innerHTML = `
      <div class="stealth-overlay-card intro">
        <span class="stealth-overlay-kicker">LEVEL ${currentLevel.number}</span>
        <h2>${currentLevel.title}</h2>
        <p>${currentLevel.subtitle}</p>
        <div class="stealth-tip-row"><span>빨강</span> 사장님 시야 · <span>주황</span> 소음 · <span>노랑</span> 센서등</div>
        <div class="stealth-star-rule"><span>★★★</span> ${formatStealthSeconds(currentLevel.targetSeconds)} 이내 · 경계 0회 · 무실패</div>
        <button id="stealth-start" type="button">${currentLevel.number === 1 ? '몰래 퇴근 시작' : `${currentLevel.number}단계 시작`}</button>
        <small>Space 또는 Enter로 시작</small>
      </div>`;
    overlay.classList.add('visible');
    document.querySelector('#stealth-start')?.addEventListener('click', startAttempt);
  }

  function loadLevel(index: number, nextStatus: GameStatus = 'intro'): void {
    resetVersion += 1;
    currentLevelIndex = clamp(index, 0, STEALTH_LEVELS.length - 1);
    currentLevel = STEALTH_LEVELS[currentLevelIndex];
    levelRetries = 0;
    runAlerts = 0;
    levelNumber.textContent = `LEVEL ${currentLevel.number} / ${STEALTH_LEVELS.length}`;
    levelTitle.textContent = currentLevel.title;
    starTarget.textContent = `★★★ ${formatStealthSeconds(currentLevel.targetSeconds)} · 경계 0 · 무실패`;
    updateLevelNavigation();
    prepareAttempt(nextStatus);
    if (nextStatus === 'intro') showIntroOverlay();
  }

  function prepareAttempt(nextStatus: GameStatus): void {
    resetVersion += 1;
    player.x = currentLevel.start.x;
    player.y = currentLevel.start.y;
    player.facing = 0;
    runtimeBosses = createRuntimeBosses();
    noisePulses = [];
    occupiedNoiseZones = new Set();
    distractorCooldowns = new Map();
    detection = 0;
    warningActive = false;
    runAlerts = 0;
    attemptSeconds = 0;
    displayedTenths = -1;
    retryReady = false;
    input.clear();
    status = nextStatus;
    attemptStartedAt = nextStatus === 'playing' ? performance.now() : 0;
    timeDisplay.textContent = '0.0초';
    updateStats();
    message.classList.remove('visible');
    if (nextStatus === 'playing') {
      hideOverlay();
      canvas.focus({ preventScroll: true });
    }
  }

  function startAttempt(): void {
    prepareAttempt('playing');
  }

  function retryAttempt(): void {
    if (status !== 'dead' || !retryReady) return;
    startAttempt();
  }

  function triggerFailure(reason: string): void {
    if (status !== 'playing') return;
    status = 'dead';
    input.clear();
    levelRetries += 1;
    totalRetries += 1;
    retryReady = false;
    retryDisplay.textContent = String(levelRetries);
    overlay.innerHTML = `
      <div class="stealth-overlay-card retry">
        <span class="stealth-overlay-kicker">RETRY ${levelRetries}</span>
        <div class="stealth-fail-animation" aria-hidden="true"><span>!</span></div>
        <h2>딱 걸렸습니다</h2>
        <p>${reason}</p>
        <button id="stealth-retry" class="stealth-retry-button" type="button" disabled aria-hidden="true">다시 숨어보기</button>
        <small id="stealth-retry-help" class="stealth-retry-help" aria-live="polite"></small>
      </div>`;
    overlay.classList.add('visible');
    const retryButton = document.querySelector<HTMLButtonElement>('#stealth-retry');
    retryButton?.addEventListener('click', retryAttempt);
    const unlockVersion = ++resetVersion;
    window.setTimeout(() => {
      if (unlockVersion !== resetVersion || status !== 'dead') return;
      retryReady = true;
      if (retryButton) {
        retryButton.disabled = false;
        retryButton.removeAttribute('aria-hidden');
        retryButton.classList.add('ready');
      }
      const help = document.querySelector<HTMLElement>('#stealth-retry-help');
      if (help) { help.textContent = 'Space · Enter · R로 다시 시작'; help.classList.add('visible'); }
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 250 : RETRY_DELAY_MS);
  }

  function completeLevel(): void {
    if (status !== 'playing') return;
    status = currentLevelIndex === STEALTH_LEVELS.length - 1 ? 'finished' : 'complete';
    input.clear();
    attemptSeconds = attemptStartedAt ? (performance.now() - attemptStartedAt) / 1_000 : attemptSeconds;
    const result: StealthLevelResult = {
      stars: calculateStealthStars(attemptSeconds, levelRetries, runAlerts, currentLevel.targetSeconds),
      clearSeconds: attemptSeconds,
      retries: levelRetries,
      alerts: runAlerts,
    };
    const previous = bestResults.get(currentLevelIndex);
    const newBest = isBetterStealthResult(result, previous);
    if (newBest) {
      bestResults.set(currentLevelIndex, result);
      saveStoredResults(bestResults);
    }
    updateLevelNavigation();

    if (status === 'finished') {
      const totalStars = [...bestResults.values()].reduce((sum, item) => sum + item.stars, 0);
      overlay.innerHTML = `
        <div class="stealth-overlay-card complete final">
          <span class="stealth-overlay-kicker">20 LEVEL CLEAR</span>
          <h2>아무도 모르게 칼퇴!</h2>
          <div class="stealth-clear-stars" aria-label="마지막 레벨 별 ${result.stars}개">${renderStars(result.stars)}</div>
          <p>누적 별 ${totalStars}개 · 전체 재도전 ${totalRetries}회</p>
          <button id="stealth-restart-all" type="button">1단계부터 다시 하기</button>
          <small>사장님은 아직도 사무실에 있습니다.</small>
        </div>`;
      overlay.classList.add('visible');
      document.querySelector('#stealth-restart-all')?.addEventListener('click', () => loadLevel(0, 'playing'));
      return;
    }

    overlay.innerHTML = `
      <div class="stealth-overlay-card complete">
        <span class="stealth-overlay-kicker">LEVEL ${currentLevel.number} CLEAR</span>
        <h2>${currentLevel.number}단계 몰래 퇴근!</h2>
        <div class="stealth-clear-stars" aria-label="별 ${result.stars}개">${renderStars(result.stars)}</div>
        <p>${currentLevel.clearCopy}</p>
        <div class="stealth-clear-result">
          <span>시간 <strong>${formatStealthSeconds(result.clearSeconds)}</strong></span>
          <span>경계 <strong>${result.alerts}</strong></span>
          <span>재도전 <strong>${result.retries}</strong></span>
        </div>
        <div class="stealth-star-rule"><span>★★★</span> ${formatStealthSeconds(currentLevel.targetSeconds)} 이내 · 경계 0회 · 무실패${newBest ? ' · 최고 기록' : ''}</div>
        <button id="stealth-next" type="button" aria-keyshortcuts="Space"><span>${currentLevel.number + 1}단계로</span><kbd>SPACE</kbd></button>
        <small>Space 또는 Enter로 다음 단계</small>
      </div>`;
    overlay.classList.add('visible');
    document.querySelector('#stealth-next')?.addEventListener('click', () => loadLevel(currentLevelIndex + 1, 'playing'));
  }

  function movePlayerAxis(axis: 'x' | 'y', amount: number): void {
    player[axis] += amount;
    for (const blocker of collisionRects()) {
      if (!rectsOverlap(player, blocker)) continue;
      if (axis === 'x') player.x = amount > 0 ? blocker.x - player.w : blocker.x + blocker.w;
      else player.y = amount > 0 ? blocker.y - player.h : blocker.y + blocker.h;
    }
    player.x = clamp(player.x, 10, STEALTH_WORLD_WIDTH - player.w - 10);
    player.y = clamp(player.y, 10, STEALTH_WORLD_HEIGHT - player.h - 10);
  }

  function emitNoise(origin: Point, range: number, copy: string, countsAlert: boolean): void {
    noisePulses.push({ ...origin, age: 0, radius: 0, maxRadius: Math.min(190, range * .48) });
    for (const bossState of runtimeBosses) {
      if (distanceBetween(origin, bossState) > range) continue;
      bossState.investigateTarget = { ...origin };
      bossState.investigateLeft = 3.2;
      bossState.pauseLeft = 0;
      bossState.route = [];
      bossState.routeGoal = null;
    }
    if (countsAlert) runAlerts += 1;
    updateStats();
    showMessage(copy);
  }

  function activateDistractor(): void {
    if (status !== 'playing') return;
    const center = rectCenter(player);
    const nearby = currentLevel.distractors.find((item) => distanceBetween(center, rectCenter(item)) <= 72);
    if (!nearby) {
      showMessage('작동할 복사기나 커피머신이 가까이 없습니다.', 1.1);
      return;
    }
    if ((distractorCooldowns.get(nearby.id) ?? 0) > 0) {
      showMessage(`${nearby.label}가 다시 준비되는 중입니다.`, 1.1);
      return;
    }
    distractorCooldowns.set(nearby.id, 4.5);
    emitNoise(rectCenter(nearby), nearby.range, `${nearby.label} 소리로 순찰의 시선을 돌렸습니다.`, false);
  }

  function updateNoiseZones(isMoving: boolean, sneaking: boolean): void {
    const nextOccupied = new Set<string>();
    for (const zone of currentLevel.noiseZones) {
      if (!rectsOverlap(player, zone)) continue;
      nextOccupied.add(zone.id);
      if (!occupiedNoiseZones.has(zone.id) && isMoving && !sneaking) {
        emitNoise(rectCenter(player), 355, `${zone.label}에서 큰 소리가 났습니다!`, true);
      }
    }
    occupiedNoiseZones = nextOccupied;
  }

  function moveBoss(state: RuntimeBoss, delta: number): void {
    let investigating = state.investigateLeft > 0 && state.investigateTarget !== null;
    if (investigating) {
      state.investigateLeft -= delta;
      if (state.investigateLeft <= 0) {
        state.investigateTarget = null;
        state.route = [];
        state.routeGoal = null;
        investigating = false;
      }
    }
    if (!investigating && state.pauseLeft > 0) {
      state.pauseLeft -= delta;
      state.angle += state.scanDirection * .42 * delta;
      return;
    }
    const target = investigating ? state.investigateTarget! : state.definition.path[state.targetIndex];
    if (distanceBetween(state, target) <= 8) {
      state.x = target.x;
      state.y = target.y;
      state.route = [];
      state.routeGoal = null;
      if (investigating) state.angle += state.scanDirection * .8 * delta;
      else {
        state.targetIndex = (state.targetIndex + 1) % state.definition.path.length;
        state.pauseLeft = state.definition.pause;
        state.scanDirection = state.scanDirection === 1 ? -1 : 1;
      }
      return;
    }
    const targetChanged = !state.routeGoal || distanceBetween(state.routeGoal, target) > 1;
    if (targetChanged) {
      state.route = findGridPath(state, target, collisionRects(), STEALTH_WORLD_WIDTH, STEALTH_WORLD_HEIGHT);
      state.routeGoal = { ...target };
    }
    const routeTarget = state.route[0] ?? target;
    if (distanceBetween(state, routeTarget) <= 7 && state.route.length > 0) state.route.shift();
    const nextPoint = state.route[0] ?? target;
    const angle = Math.atan2(nextPoint.y - state.y, nextPoint.x - state.x);
    state.angle = angle;
    const speed = state.definition.speed * (investigating ? 1.1 : 1);
    state.x += Math.cos(angle) * speed * delta;
    state.y += Math.sin(angle) * speed * delta;
  }

  function lightIsActive(zone: LightZone, elapsed: number): boolean {
    return (elapsed + zone.phase) % zone.cycle < zone.onTime;
  }

  function updateDetection(delta: number): void {
    const center = rectCenter(player);
    const blockers = collisionRects();
    const sneaking = input.has('sneak');
    const inShadow = currentLevel.shadows.some((zone) => pointInRect(center, zone));
    const inActiveLight = currentLevel.lightZones.some((zone) => lightIsActive(zone, attemptSeconds) && pointInRect(center, zone));
    let seenCount = 0;
    for (const bossState of runtimeBosses) {
      if (!pointInVisionCone(bossState, bossState.angle, center, bossState.definition.visionDistance, bossState.definition.visionAngle)) continue;
      if (!hasLineOfSight(bossState, center, blockers)) continue;
      seenCount += 1;
    }

    if (seenCount > 0 || inActiveLight) {
      const sneakFactor = sneaking ? .7 : 1;
      const shadowFactor = inShadow ? .42 : 1;
      const lightFactor = inActiveLight ? 1.55 : 1;
      const bossFactor = seenCount > 0 ? 1 + (seenCount - 1) * .32 : .48;
      detection = clamp(detection + delta * currentLevel.detectionRate * sneakFactor * shadowFactor * lightFactor * bossFactor, 0, 1);
    } else {
      detection = clamp(detection - delta * .62, 0, 1);
    }

    if (detection >= .34 && !warningActive) {
      warningActive = true;
      runAlerts += 1;
      showMessage(inActiveLight && seenCount === 0 ? '센서등이 움직임을 감지했습니다!' : '사장님이 수상한 움직임을 봤습니다!');
    } else if (detection < .08) {
      warningActive = false;
    }
    updateStats();
    if (detection >= 1) triggerFailure(inActiveLight && seenCount === 0 ? '센서등 기록에 움직임이 남았습니다.' : '사장님과 눈이 마주쳤습니다.');
  }

  function update(delta: number): void {
    if (messageTime > 0) {
      messageTime -= delta;
      if (messageTime <= 0) message.classList.remove('visible');
    }
    noisePulses = noisePulses
      .map((pulse) => ({ ...pulse, age: pulse.age + delta, radius: Math.min(pulse.maxRadius, pulse.radius + delta * 230) }))
      .filter((pulse) => pulse.age < 1.15);
    for (const [id, cooldown] of distractorCooldowns) distractorCooldowns.set(id, Math.max(0, cooldown - delta));
    if (status !== 'playing') return;

    attemptSeconds = attemptStartedAt ? (performance.now() - attemptStartedAt) / 1_000 : 0;
    const directionX = (input.has('right') ? 1 : 0) - (input.has('left') ? 1 : 0);
    const directionY = (input.has('down') ? 1 : 0) - (input.has('up') ? 1 : 0);
    const moving = directionX !== 0 || directionY !== 0;
    const sneaking = input.has('sneak');
    if (moving) {
      const length = Math.hypot(directionX, directionY);
      const speed = sneaking ? SNEAK_SPEED : WALK_SPEED;
      const moveX = directionX / length * speed * delta;
      const moveY = directionY / length * speed * delta;
      player.facing = Math.atan2(moveY, moveX);
      movePlayerAxis('x', moveX);
      movePlayerAxis('y', moveY);
    }
    updateNoiseZones(moving, sneaking);
    for (const bossState of runtimeBosses) moveBoss(bossState, delta);
    updateDetection(delta);
    if (rectsOverlap(player, currentLevel.exit)) completeLevel();
  }

  function drawBackground(): void {
    const gradient = context.createLinearGradient(0, 0, STEALTH_WORLD_WIDTH, STEALTH_WORLD_HEIGHT);
    gradient.addColorStop(0, '#111d2c');
    gradient.addColorStop(1, '#17243a');
    context.fillStyle = gradient;
    context.fillRect(0, 0, STEALTH_WORLD_WIDTH, STEALTH_WORLD_HEIGHT);
    context.strokeStyle = 'rgba(166,196,230,.055)';
    context.lineWidth = 1;
    for (let x = 0; x <= STEALTH_WORLD_WIDTH; x += 40) { context.beginPath(); context.moveTo(x, 0); context.lineTo(x, STEALTH_WORLD_HEIGHT); context.stroke(); }
    for (let y = 0; y <= STEALTH_WORLD_HEIGHT; y += 40) { context.beginPath(); context.moveTo(0, y); context.lineTo(STEALTH_WORLD_WIDTH, y); context.stroke(); }
    context.fillStyle = 'rgba(255,255,255,.025)';
    context.font = '800 160px "Lexend Deca", sans-serif';
    context.textAlign = 'center';
    context.fillText(String(currentLevel.number).padStart(2, '0'), STEALTH_WORLD_WIDTH / 2, 335);
  }

  function drawZones(): void {
    for (const zone of currentLevel.shadows) {
      context.fillStyle = 'rgba(3,8,18,.35)';
      roundedRect(context, zone, 12);
      context.fill();
      context.strokeStyle = 'rgba(130,151,184,.12)';
      context.stroke();
    }
    for (const zone of currentLevel.noiseZones) {
      context.fillStyle = 'rgba(255,174,74,.12)';
      roundedRect(context, zone, 8);
      context.fill();
      context.strokeStyle = 'rgba(255,174,74,.42)';
      context.setLineDash([8, 7]);
      context.stroke();
      context.setLineDash([]);
      context.fillStyle = 'rgba(255,196,112,.72)';
      context.font = '600 10px "Noto Sans KR", sans-serif';
      context.textAlign = 'center';
      context.fillText(zone.label, zone.x + zone.w / 2, zone.y + zone.h / 2 + 3);
    }
    for (const zone of currentLevel.lightZones) {
      const active = lightIsActive(zone, attemptSeconds);
      context.fillStyle = active ? 'rgba(255,215,92,.19)' : 'rgba(255,255,255,.025)';
      roundedRect(context, zone, 8);
      context.fill();
      context.strokeStyle = active ? 'rgba(255,215,92,.52)' : 'rgba(255,255,255,.08)';
      context.stroke();
      context.fillStyle = active ? '#ffe183' : '#697489';
      context.font = '700 9px "Noto Sans KR", sans-serif';
      context.textAlign = 'center';
      context.fillText(active ? '센서 ON' : '센서 OFF', zone.x + zone.w / 2, zone.y + 14);
    }
  }

  function drawVision(): void {
    for (const bossState of runtimeBosses) {
      const half = bossState.definition.visionAngle / 2;
      context.fillStyle = 'rgba(255,91,111,.17)';
      context.beginPath();
      context.moveTo(bossState.x, bossState.y);
      context.arc(bossState.x, bossState.y, bossState.definition.visionDistance, bossState.angle - half, bossState.angle + half);
      context.closePath();
      context.fill();
      context.strokeStyle = 'rgba(255,111,126,.32)';
      context.lineWidth = 1.5;
      context.stroke();
    }
  }

  function drawExit(): void {
    const exit = currentLevel.exit;
    context.save();
    context.shadowColor = 'rgba(73,224,162,.55)';
    context.shadowBlur = 20;
    context.fillStyle = '#49e0a2';
    roundedRect(context, exit, 10);
    context.fill();
    context.restore();
    context.fillStyle = '#08251c';
    context.fillRect(exit.x + 8, exit.y + 30, exit.w - 16, exit.h - 35);
    context.fillStyle = '#08251c';
    context.font = '700 14px "Noto Sans KR", sans-serif';
    context.textAlign = 'center';
    context.fillText('퇴근', exit.x + exit.w / 2, exit.y + 22);
  }

  function drawFurniture(item: Furniture): void {
    const colors: Record<Furniture['kind'], [string, string]> = {
      desk: ['#2d3b54', '#52617a'],
      cabinet: ['#35435b', '#65728a'],
      sofa: ['#3c3a5c', '#716e93'],
      plant: ['#24483f', '#4f8a71'],
      partition: ['#26364d', '#53677f'],
    };
    const [base, edge] = colors[item.kind];
    context.fillStyle = base;
    roundedRect(context, item, item.kind === 'plant' ? 16 : 9);
    context.fill();
    context.strokeStyle = edge;
    context.lineWidth = 2;
    context.stroke();
    context.fillStyle = edge;
    if (item.kind === 'desk') context.fillRect(item.x + 14, item.y + 13, item.w - 28, 6);
    else if (item.kind === 'cabinet') context.fillRect(item.x + 12, item.y + item.h / 2, item.w - 24, 2);
    else if (item.kind === 'partition') context.fillRect(item.x + item.w / 2 - 2, item.y + 8, 4, item.h - 16);
    context.fillStyle = 'rgba(221,230,242,.5)';
    context.font = '600 9px "Noto Sans KR", sans-serif';
    context.textAlign = 'center';
    context.fillText(furnitureLabel(item.kind), item.x + item.w / 2, item.y + item.h / 2 + 3);
  }

  function drawDistractor(item: Distractor): void {
    const ready = (distractorCooldowns.get(item.id) ?? 0) <= 0;
    context.fillStyle = ready ? '#dbe6ed' : '#617080';
    roundedRect(context, item, 8);
    context.fill();
    context.strokeStyle = ready ? '#92a8b6' : '#465464';
    context.lineWidth = 2;
    context.stroke();
    context.fillStyle = ready ? '#ff8b7c' : '#8b96a3';
    context.fillRect(item.x + 9, item.y + 10, item.w - 18, 9);
    context.fillStyle = '#1b2635';
    context.font = '700 9px "Noto Sans KR", sans-serif';
    context.textAlign = 'center';
    context.fillText(item.label, item.x + item.w / 2, item.y + 37);
    if (distanceBetween(rectCenter(player), rectCenter(item)) <= 72 && status === 'playing') {
      context.fillStyle = '#fff';
      context.font = '700 10px "Lexend Deca", sans-serif';
      context.fillText('E', item.x + item.w / 2, item.y - 8);
    }
  }

  function drawBoss(state: RuntimeBoss): void {
    context.save();
    context.translate(state.x, state.y);
    context.rotate(state.angle);
    context.fillStyle = '#ff6f7e';
    context.beginPath();
    context.arc(0, 0, 20, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = '#3d1a22';
    context.beginPath();
    context.moveTo(13, 0);
    context.lineTo(3, -5);
    context.lineTo(3, 5);
    context.closePath();
    context.fill();
    context.restore();
    context.fillStyle = '#fff';
    context.font = '700 9px "Noto Sans KR", sans-serif';
    context.textAlign = 'center';
    context.fillText(state.investigateTarget ? '뭐지?' : '사장', state.x, state.y + 3);
  }

  function drawPlayer(): void {
    const sneaking = input.has('sneak') && status === 'playing';
    context.save();
    context.translate(player.x + player.w / 2, player.y + player.h / 2);
    context.rotate(player.facing);
    context.fillStyle = sneaking ? '#ab9bff' : '#8065ff';
    context.shadowColor = 'rgba(128,101,255,.38)';
    context.shadowBlur = 11;
    roundedRect(context, { x: -player.w / 2, y: -player.h / 2, w: player.w, h: player.h }, 8);
    context.fill();
    context.shadowBlur = 0;
    context.fillStyle = '#fff';
    context.fillRect(3, -8, 5, 5);
    context.fillRect(3, 3, 5, 5);
    context.fillStyle = '#ffc95c';
    context.beginPath();
    context.moveTo(11, 0);
    context.lineTo(4, -4);
    context.lineTo(4, 4);
    context.closePath();
    context.fill();
    context.restore();
    context.fillStyle = 'rgba(255,255,255,.6)';
    context.font = '700 9px "Noto Sans KR", sans-serif';
    context.textAlign = 'center';
    context.fillText(sneaking ? '살금' : '나', player.x + player.w / 2, player.y - 7);
  }

  function drawNoisePulses(): void {
    for (const pulse of noisePulses) {
      context.strokeStyle = `rgba(255,190,96,${Math.max(0, .65 - pulse.age * .55)})`;
      context.lineWidth = 3;
      context.beginPath();
      context.arc(pulse.x, pulse.y, pulse.radius, 0, Math.PI * 2);
      context.stroke();
    }
  }

  function draw(): void {
    drawBackground();
    drawZones();
    drawExit();
    drawVision();
    for (const item of currentLevel.furniture) drawFurniture(item);
    for (const item of currentLevel.distractors) drawDistractor(item);
    for (const bossState of runtimeBosses) drawBoss(bossState);
    drawPlayer();
    drawNoisePulses();
    if (detection > 0) {
      context.strokeStyle = `rgba(255,79,99,${detection * .72})`;
      context.lineWidth = 9 + detection * 11;
      context.strokeRect(4, 4, STEALTH_WORLD_WIDTH - 8, STEALTH_WORLD_HEIGHT - 8);
    }
  }

  function frame(now: number): void {
    const delta = Math.min(.033, Math.max(0, (now - lastFrameAt) / 1_000));
    lastFrameAt = now;
    update(delta);
    if (status === 'playing') {
      const tenths = Math.floor(attemptSeconds * 10);
      if (tenths !== displayedTenths) {
        displayedTenths = tenths;
        timeDisplay.textContent = formatStealthSeconds(attemptSeconds);
      }
    }
    draw();
    requestAnimationFrame(frame);
  }

  const keyMap: Record<string, string> = {
    ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right',
    ArrowUp: 'up', KeyW: 'up', ArrowDown: 'down', KeyS: 'down',
  };
  window.addEventListener('keydown', (event) => {
    const move = keyMap[event.code];
    if (move) { event.preventDefault(); input.add(move); }
    if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') input.add('sneak');
    if (event.code === 'KeyE' && !event.repeat) activateDistractor();
    if (event.code === 'Space' && !event.repeat) {
      event.preventDefault();
      if (status === 'intro') startAttempt();
      else if (status === 'complete') loadLevel(currentLevelIndex + 1, 'playing');
      else if (status === 'dead') retryAttempt();
    }
    if (event.code === 'Enter' && !event.repeat) {
      if (status === 'intro') startAttempt();
      else if (status === 'complete') loadLevel(currentLevelIndex + 1, 'playing');
      else if (status === 'dead') retryAttempt();
    }
    if (event.code === 'KeyR' && !event.repeat) {
      if (status === 'playing') triggerFailure('더 안전한 경로를 고르기 위해 자리로 돌아갑니다.');
      else if (status === 'dead') retryAttempt();
    }
  });
  window.addEventListener('keyup', (event) => {
    const move = keyMap[event.code];
    if (move) input.delete(move);
    if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') input.delete('sneak');
  });
  window.addEventListener('blur', () => input.clear());

  document.querySelectorAll<HTMLButtonElement>('[data-stealth-move]').forEach((button) => {
    const move = button.dataset.stealthMove!;
    button.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      button.setPointerCapture(event.pointerId);
      button.classList.add('pressed');
      input.add(move);
    });
    const release = () => { button.classList.remove('pressed'); input.delete(move); };
    button.addEventListener('pointerup', release);
    button.addEventListener('pointercancel', release);
    button.addEventListener('lostpointercapture', release);
  });

  const sneakButton = document.querySelector<HTMLButtonElement>('#stealth-sneak');
  sneakButton?.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    sneakButton.setPointerCapture(event.pointerId);
    sneakButton.classList.add('pressed');
    input.add('sneak');
  });
  const releaseSneak = () => { sneakButton?.classList.remove('pressed'); input.delete('sneak'); };
  sneakButton?.addEventListener('pointerup', releaseSneak);
  sneakButton?.addEventListener('pointercancel', releaseSneak);
  sneakButton?.addEventListener('lostpointercapture', releaseSneak);
  document.querySelector('#stealth-action')?.addEventListener('click', activateDistractor);

  document.querySelectorAll<HTMLButtonElement>('[data-stealth-level]').forEach((button) => {
    button.addEventListener('click', () => loadLevel(Number(button.dataset.stealthLevel), 'intro'));
  });

  updateLevelNavigation();
  prepareAttempt('intro');
  showIntroOverlay();
  requestAnimationFrame(frame);
}
